import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import HeroSection from "@/components/HeroSection";
import ProductCard from "@/components/ProductCard";
import { ArrowRight, Truck, Boxes, CircleDot, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  {
    id: "skeleton_trailers",
    name: "Skeleton Trailers",
    desc: "20ft & 40ft skeleton trailers for container transport",
    icon: Truck,
    image: "https://images.unsplash.com/photo-1756888218988-41f62ab420ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxza2VsZXRvbiUyMHRyYWlsZXIlMjBjb250YWluZXIlMjBjaGFzc2lzfGVufDB8fHx8MTc3NjM1MTQ1Nnww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "flatbed_trailers",
    name: "Flatbed Trailers",
    desc: "20ft & 40ft flatbed trailers for container transport",
    icon: Boxes,
    image: "https://images.unsplash.com/photo-1660643868046-16387cc693e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxmbGF0YmVkJTIwdHJhaWxlciUyMHNlbWklMjB0cnVja3xlbnwwfHx8fDE3NzYzNTE0NzF8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "metals",
    name: "Ferrous & Non-Ferrous Metals",
    desc: "Trading in ferrous and non-ferrous metals",
    icon: CircleDot,
    image: "https://images.unsplash.com/photo-1697698532634-ea59b636ccea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxzdGVlbCUyMGNvaWxzJTIwbWV0YWwlMjBmYWN0b3J5JTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzYzNTE0Nzd8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "cargo",
    name: "General Cargo",
    desc: "General cargo trading services",
    icon: Package,
    image: "https://images.unsplash.com/photo-1774929105935-3f431802894e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxjYXJnbyUyMHNoaXBwaW5nJTIwY29udGFpbmVyJTIwcG9ydCUyMGRvY2t8ZW58MHx8fHwxNzc2MzUxNDkwfDA&ixlib=rb-4.1.0&q=85",
  },
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const res = await axios.get(`${API}/products`, { params: { featured: true, limit: 6 } });
        setFeatured(res.data.products || []);
      } catch (err) {
        console.error("Failed to fetch featured products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />

      {/* Categories Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-12">
          <p className="text-[#DC143C] text-sm uppercase tracking-[0.2em] font-semibold mb-3">
            What We Offer
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Our Trading Categories
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="categories-grid">
          {CATEGORIES.map((cat, i) => {
            const Icon = cat.icon;
            return (
              <Link
                key={cat.id}
                to={`/catalog/${cat.id}`}
                data-testid={`category-card-${cat.id}`}
                className="group relative overflow-hidden rounded-lg border border-white/10 hover:border-[#DC143C]/50 transition-all duration-300 hover:shadow-[0_0_24px_rgba(220,20,60,0.15)]"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="absolute inset-0">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/70 to-transparent" />
                </div>
                <div className="relative p-6 md:p-8 min-h-[200px] flex flex-col justify-end">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-md bg-[#DC143C]/10 border border-[#DC143C]/30 flex items-center justify-center">
                      <Icon size={20} className="text-[#DC143C]" />
                    </div>
                    <h3 className="text-white font-bold text-lg tracking-tight">{cat.name}</h3>
                  </div>
                  <p className="text-[#9CA3AF] text-sm mb-4">{cat.desc}</p>
                  <span className="inline-flex items-center text-[#DC143C] text-sm font-medium group-hover:gap-2 gap-1 transition-all">
                    Browse <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[#DC143C] text-sm uppercase tracking-[0.2em] font-semibold mb-3">
              Featured Inventory
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              Latest Listings
            </h2>
          </div>
          <Link
            to="/catalog"
            data-testid="view-all-link"
            className="hidden md:inline-flex items-center gap-2 text-[#DC143C] text-sm font-medium hover:gap-3 transition-all"
          >
            View All <ArrowRight size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#0A0A12] border border-white/10 rounded-lg overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-[#1E1E28]" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-[#1E1E28] rounded w-1/3" />
                  <div className="h-4 bg-[#1E1E28] rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : featured.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-products-grid">
            {featured.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-[#9CA3AF]">No featured products available yet.</p>
            <Link to="/contact" className="text-[#DC143C] text-sm font-medium mt-2 inline-block">
              Contact us for inquiries
            </Link>
          </div>
        )}

        <Link
          to="/catalog"
          data-testid="mobile-view-all"
          className="md:hidden flex items-center justify-center gap-2 text-[#DC143C] text-sm font-medium mt-8"
        >
          View All Products <ArrowRight size={14} />
        </Link>
      </section>

      {/* CTA Banner */}
      <section className="border-t border-b border-white/10 bg-[#0A0A12]">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">
            Looking for Something Specific?
          </h2>
          <p className="text-[#9CA3AF] text-base mb-8 max-w-lg mx-auto">
            We source trailers, metals, and cargo worldwide. Tell us what you need and we'll make it happen.
          </p>
          <Link
            to="/contact"
            data-testid="cta-contact-us"
            className="inline-flex items-center gap-2 bg-[#DC143C] text-white font-semibold tracking-wide px-7 py-3.5 rounded-md hover:bg-[#B01030] transition-colors"
          >
            Contact Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
